import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Header, SectionLabel } from "../components/Brand";
import { api, formatIST, BACKEND_URL } from "../lib/api";
import { Loader2, ArrowLeft, Download, FileText, Search } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

export default function AdminFilesPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = localStorage.getItem("bh_admin_token");
    if (!t) {
      navigate("/admin");
      return;
    }
    api
      .get("/admin/files")
      .then((r) => setFiles(r.data.files || []))
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/admin");
        else toast.error("Failed to load files");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return files;
    return files.filter(
      (f) =>
        (f.candidate_name || "").toLowerCase().includes(s) ||
        (f.candidate_email || "").toLowerCase().includes(s) ||
        (f.candidate_code || "").toLowerCase().includes(s) ||
        (f.original_filename || "").toLowerCase().includes(s)
    );
  }, [files, q]);

  const downloadUrl = (path) =>
    `${BACKEND_URL}/api/files/download?path=${encodeURIComponent(path)}`;

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="admin-files-page">
      <Header
        right={
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Dashboard
          </button>
        }
      />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <SectionLabel className="mb-2">CV Vault</SectionLabel>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
              All Resumes — {files.length}
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              Every CV uploaded by candidates, in one place.
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-zinc-500" />
            <input
              data-testid="files-search"
              className="bh-input pl-10"
              placeholder="Search by name, email, code, file…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[0.65rem] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Candidate</th>
                  <th className="text-left px-4 py-3">Branch / Year</th>
                  <th className="text-left px-4 py-3">File</th>
                  <th className="text-left px-4 py-3">Size</th>
                  <th className="text-left px-4 py-3">Uploaded</th>
                  <th className="text-left px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-zinc-500">
                      <Loader2 className="inline w-5 h-5 animate-spin" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-zinc-500 text-sm">
                      No CVs uploaded yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((f) => (
                    <tr key={f.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 font-mono text-xs text-zinc-400">{f.candidate_code || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="text-white font-semibold">{f.candidate_name || "—"}</div>
                        <div className="text-xs text-zinc-500">{f.candidate_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-400">
                        {f.branch}
                        <div className="text-zinc-600">{f.year}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-200 max-w-[280px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
                          <span className="truncate">{f.original_filename}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-400 font-mono">{fmtSize(f.size)}</td>
                      <td className="px-4 py-3 text-xs text-zinc-400">{formatIST(f.created_at)}</td>
                      <td className="px-4 py-3 flex gap-2">
                        <a
                          href={downloadUrl(f.storage_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-white border border-white/20 px-2 py-1 rounded-sm hover:bg-white/5"
                          data-testid={`download-cv-${f.id}`}
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </a>
                        {f.candidate_id && (
                          <button
                            onClick={() => navigate(`/admin/candidate/${f.candidate_id}`)}
                            className="text-xs text-zinc-400 hover:text-white"
                          >
                            View profile
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
